import { Document, Page, View } from "@react-pdf/renderer";
import { getFinancialReportMeta } from "../../../../hooks/useFinancialReport";
import {
  ReportPeriodHeader,
  ReportTable,
  ReportTotals,
  formatCurrency,
  formatDate,
  formatMonthLabel,
  pdfStyles,
} from "./shared";

function HonorariosRecebidosDocument({ data }) {
  const rows = data?.items ?? [];
  const totals = data?.totals ?? {};

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page} orientation="landscape">
        <View style={pdfStyles.section}>
          <ReportPeriodHeader
            title="Honorários Recebidos"
            from={data?.from}
            to={data?.to}
          />
          <ReportTable
            rows={rows}
            columns={[
              {
                key: "client",
                header: "Cliente",
                getContent: (r) => r.client || "—",
              },
              {
                key: "lawsuit",
                header: "Processo",
                getContent: (r) => r.lawsuit_number || "—",
              },
              {
                key: "type",
                header: "Tipo",
                getContent: (r) => r.type || "—",
              },
              {
                key: "contracted",
                header: "Contratado",
                getContent: (r) => formatCurrency(r.contracted_value),
              },
              {
                key: "received",
                header: "Recebido",
                getContent: (r) => formatCurrency(r.received_value),
              },
              {
                key: "saldo",
                header: "Saldo",
                getContent: (r) => formatCurrency(r.saldo),
              },
              {
                key: "date",
                header: "Atualizado",
                getContent: (r) => formatDate(r.updated_at),
              },
            ]}
          />
          <ReportTotals
            lines={[
              { label: "Itens", value: String(totals.items_count ?? rows.length) },
              {
                label: "Total contratado",
                value: formatCurrency(totals.contracted_value),
              },
              {
                label: "Total recebido",
                value: formatCurrency(totals.received_value),
              },
              { label: "Saldo", value: formatCurrency(totals.saldo) },
            ]}
          />
        </View>
      </Page>
    </Document>
  );
}

function HonorariosPorClienteDocument({ data }) {
  const rows = data?.items ?? [];
  const totals = data?.totals ?? {};

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <ReportPeriodHeader
            title="Honorários por Cliente"
            from={data?.from}
            to={data?.to}
          />
          <ReportTable
            rows={rows}
            columns={[
              {
                key: "client",
                header: "Cliente",
                getContent: (r) => r.client || "—",
              },
              {
                key: "count",
                header: "Qtd.",
                getContent: (r) => String(r.items_count ?? 0),
              },
              {
                key: "contracted",
                header: "Contratado",
                getContent: (r) => formatCurrency(r.contracted_value),
              },
              {
                key: "received",
                header: "Recebido",
                getContent: (r) => formatCurrency(r.received_value),
              },
              {
                key: "saldo",
                header: "Saldo",
                getContent: (r) => formatCurrency(r.saldo),
              },
            ]}
          />
          <ReportTotals
            lines={[
              {
                label: "Clientes",
                value: String(totals.clients_count ?? rows.length),
              },
              {
                label: "Itens",
                value: String(totals.items_count ?? 0),
              },
              {
                label: "Total contratado",
                value: formatCurrency(totals.contracted_value),
              },
              {
                label: "Total recebido",
                value: formatCurrency(totals.received_value),
              },
              { label: "Saldo", value: formatCurrency(totals.saldo) },
            ]}
          />
        </View>
      </Page>
    </Document>
  );
}

function MonthlyTotalDocument({ title, data }) {
  const rows = data?.months ?? [];
  const totals = data?.totals ?? {};

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <ReportPeriodHeader title={title} from={data?.from} to={data?.to} />
          <ReportTable
            rows={rows}
            columns={[
              {
                key: "month",
                header: "Mês",
                getContent: (r) => formatMonthLabel(r),
              },
              {
                key: "count",
                header: "Qtd.",
                getContent: (r) => String(r.items_count ?? 0),
              },
              {
                key: "total",
                header: "Total",
                getContent: (r) => formatCurrency(r.total),
              },
            ]}
          />
          <ReportTotals
            lines={[
              {
                label: "Itens",
                value: String(totals.items_count ?? 0),
              },
              { label: "Total", value: formatCurrency(totals.total) },
            ]}
          />
        </View>
      </Page>
    </Document>
  );
}

function FluxoDeCaixaDocument({ data }) {
  const rows = data?.months ?? [];
  const totals = data?.totals ?? {};

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <ReportPeriodHeader
            title="Fluxo de Caixa"
            from={data?.from}
            to={data?.to}
          />
          <ReportTable
            rows={rows}
            columns={[
              {
                key: "month",
                header: "Mês",
                getContent: (r) => formatMonthLabel(r),
              },
              {
                key: "entradas",
                header: "Entradas",
                getContent: (r) => formatCurrency(r.entradas),
              },
              {
                key: "saidas",
                header: "Saídas",
                getContent: (r) => formatCurrency(r.saidas),
              },
              {
                key: "saldo",
                header: "Saldo",
                getContent: (r) => formatCurrency(r.saldo),
              },
            ]}
          />
          <ReportTotals
            lines={[
              { label: "Entradas", value: formatCurrency(totals.entradas) },
              { label: "Saídas", value: formatCurrency(totals.saidas) },
              { label: "Saldo", value: formatCurrency(totals.saldo) },
            ]}
          />
        </View>
      </Page>
    </Document>
  );
}

/** Renders the PDF document for the selected report type. */
export function FinancialReportDocument({ type, data }) {
  switch (type) {
    case "honorarios-recebidos":
      return <HonorariosRecebidosDocument data={data} />;
    case "honorarios-por-cliente":
      return <HonorariosPorClienteDocument data={data} />;
    case "receitas-por-mes":
      return <MonthlyTotalDocument title="Receitas por Mês" data={data} />;
    case "despesas-por-mes":
      return <MonthlyTotalDocument title="Despesas por Mês" data={data} />;
    case "fluxo-de-caixa":
      return <FluxoDeCaixaDocument data={data} />;
    default: {
      const meta = getFinancialReportMeta(type);
      return (
        <Document>
          <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.section}>
              <ReportPeriodHeader
                title={meta.label}
                from={data?.from}
                to={data?.to}
              />
            </View>
          </Page>
        </Document>
      );
    }
  }
}
